import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  input,
  linkedSignal,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DocumentContentViewer } from '@app/features/documents/document-content-viewer/document-content-viewer';
import { displayMimeType, downloadFileName } from '@app/features/documents/document-file-name';
import { CdTDocument } from '@app/features/documents/document.model';
import { DownloadService } from '@app/features/download.service';
import { LabelValue } from '@app/shared/forms/label-value/label-value';
import { DocumentTagLabelPipe } from '../document-tag-label-pipe';
import { XIconButton } from '@app/shared/buttons/x-icon-button/x-icon-button';
import { LucideExternalLink } from '@lucide/angular';

@Component({
  selector: 'app-document-viewer',
  imports: [
    CommonModule,
    DocumentContentViewer,
    LabelValue,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    DocumentTagLabelPipe,
    XIconButton,
    LucideExternalLink,
  ],
  templateUrl: './document-viewer.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./document-viewer.scss'],
})
export class DocumentViewer {
  readonly document = input.required<CdTDocument>();
  readonly closed = output<void>();
  private readonly downloadService = inject(DownloadService);

  /** Fichier du document affiché, connu une fois chargé par la visionneuse. */
  readonly blob = linkedSignal<CdTDocument, Blob | undefined>({
    source: this.document,
    computation: (document) => document.blob,
  });

  closePreview() {
    this.closed.emit();
  }

  /**
   * Ouvre le fichier d'origine dans un nouvel onglet, où le navigateur offre l'impression et la
   * recherche dans le texte (RG-document-ouverture-dans-un-onglet). Le fichier est présenté sous
   * le type du document : servi sans type précis, le navigateur le téléchargerait au lieu de
   * l'afficher. Un `scan.pdf` sans type précis s'ouvre en PDF, comme la visionneuse l'affiche.
   */
  openInNewTab() {
    const blob = this.blob();
    if (!blob) {
      return;
    }
    const type = displayMimeType(this.document(), blob);
    const typed = blob.type === type ? blob : new Blob([blob], { type });
    this.downloadService.openFile(typed);
  }

  download() {
    const blob = this.blob();
    if (!blob) {
      return;
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadFileName(this.document(), blob);
    a.click();
    window.URL.revokeObjectURL(url);
  }
}
